(() => {
  // app/scripts/coveo-search-analytics.js
  var ANALYTICS_EVENT_TYPE = "search";
  var CoveoSearchAnalytics = class {
    constructor(engine) {
      this.engine = engine;
    }
    get _config() {
      const { organizationId, accessToken, analytics } = this.engine.configuration;
      return {
        organizationId,
        accessToken,
        trackingId: analytics.trackingId
      };
    }
    get _endpoint() {
      const { organizationId } = this._config;
      return `https://${organizationId}.analytics.org.coveo.com/rest/ua/v15/analytics/${ANALYTICS_EVENT_TYPE}`;
    }
    get _searchState() {
      return this.engine.store.getState().commerceSearch || {};
    }
    _getClientId() {
      try {
        return this.engine.relay.getMeta("").clientId;
      } catch (error) {
        console.warn(`[CoveoSearchAnalytics] Failed to get clientId:`, error);
        return void 0;
      }
    }
    _getFacetState(pendingChange = null) {
      const { facets } = this._searchState;
      if (!Array.isArray(facets))
        return [];
      return facets.flatMap(
        (facet) => facet.values.reduce((acc, v, index) => {
          let isSelected = v.state === "selected";
          if (pendingChange && pendingChange.facetId === facet.facetId && pendingChange.value === v.value) {
            isSelected = pendingChange.action === "select";
          }
          if (isSelected) {
            acc.push({
              field: facet.facetId,
              id: facet.facetId,
              title: facet.displayName || facet.facetId,
              facetType: "specific",
              value: v.value,
              valuePosition: index + 1,
              displayValue: v.value,
              state: "selected"
            });
          }
          return acc;
        }, [])
      );
    }
    _buildPayload(actionCause, customData = {}, { pendingChange = null, responseId: responseIdOverride } = {}) {
      const { trackingId } = this._config;
      const state = this.engine.store.getState();
      const { responseId, queryExecuted } = state.commerceSearch || {};
      const language = state.commerceContext?.language || document.documentElement.lang || "en";
      return {
        actionCause,
        searchQueryUid: responseIdOverride || responseId,
        queryText: queryExecuted || "",
        language,
        originLevel1: trackingId,
        originLevel2: "default",
        originLevel3: window.location.href,
        clientId: this._getClientId(),
        anonymous: false,
        responseTime: 0,
        customData,
        facetState: this._getFacetState(pendingChange ?? null)
      };
    }
    async logSearchEvent(actionCause, customData = {}, options = {}) {
      const { accessToken } = this._config;
      if (!accessToken)
        return;
      const payload = this._buildPayload(actionCause, customData, options);
      try {
        const response = await fetch(this._endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          console.warn(`[CoveoSearchAnalytics] ${actionCause} failed:`, response.status);
        }
      } catch (error) {
        console.warn(`[CoveoSearchAnalytics] ${actionCause} error:`, error);
      }
    }
    logInterfaceLoad() {
      return this.logSearchEvent("interfaceLoad");
    }
    logSearchboxSubmit() {
      return this.logSearchEvent("searchboxSubmit");
    }
    logSearchFromLink() {
      return this.logSearchEvent("searchFromLink");
    }
    logOmniboxAnalytics(metadata = {}) {
      return this.logSearchEvent("omniboxAnalytics", metadata);
    }
    logRecommendationInterfaceLoad(responseId) {
      return this.logSearchEvent("recommendationInterfaceLoad", {}, { responseId });
    }
    logFacetSelect(facetId, facetValue) {
      return this.logSearchEvent("facetSelect", {
        facetId,
        facetValue,
        facetField: facetId
      }, { pendingChange: { facetId, value: facetValue, action: "select" } });
    }
    logFacetDeselect(facetId, facetValue) {
      return this.logSearchEvent("facetDeselect", {
        facetId,
        facetValue,
        facetField: facetId
      }, { pendingChange: { facetId, value: facetValue, action: "deselect" } });
    }
    logFacetClearAll() {
      return this.logSearchEvent("facetClearAll");
    }
  };
  var coveo_search_analytics_default = CoveoSearchAnalytics;

  // app/scripts/components/coveo/CoveoBase.js
  var { delay } = theme;
  var COVEO_MAX_RESULTS = 5e3;
  var CoveoBase = class extends HTMLElement {
    constructor(buildControllerModel, options = {}) {
      super();
      this.options = {
        pageSize: +this.dataset.pageSize || 12,
        ...options
      };
      this.buildControllerModel = buildControllerModel;
      this.selectors = {
        results: this.getAttribute("data-results") || "#collection-results",
        facets: this.getAttribute("data-facets") || "#facets",
        pagination: this.getAttribute("data-pagination") || "#pagination",
        itemsPerPage: this.getAttribute("data-items-per-page") || "[name='products_per_page']",
        sort: this.getAttribute("data-sort") || ".js-sort-by"
      };
      this.currentPage = 0;
      this.itemsPerPage = this.options.defaultItemsPerPage;
      this.products = [];
      this.firstRender = false;
      this.isFirstUrlUpdate = true;
      this.lastSyncedUrl = null;
      this.popstateTimeout = null;
      this.lastProductFingerprint = null;
      this.handlePageChange = this.handlePageChange.bind(this);
      this.handleItemsPerPageChange = this.handleItemsPerPageChange.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }
    async connectedCallback() {
      this.isFirstUrlUpdate = true;
      this.lastSyncedUrl = null;
      if (this.popstateTimeout) {
        clearTimeout(this.popstateTimeout);
      }
      const searchToken = await CoveoUtils.getSearchToken();
      if (!searchToken) {
        console.error("Failed to fetch search token");
        return;
      }
      this.resultsContainer = document.querySelector(this.selectors.results);
      this.sortBy = document.querySelectorAll(this.selectors.sort);
      this.facets = document.querySelector(this.selectors.facets);
      this.itemsPerPageSelect = document.querySelectorAll(this.selectors.itemsPerPage);
      this.pagination = this.querySelector(this.selectors.pagination);
      this.banner = this.resultsContainer.querySelector(".js-banner");
      if (this.banner) {
        this.banner?.classList?.remove("hidden");
        this.banner?.remove();
        this.bannerIndex = +this.banner.dataset.index;
      }
      this.engine = await window.configureHeadless(searchToken);
      this.analytics = new coveo_search_analytics_default(this.engine);
      this.controller = this.buildControllerModel(this.engine);
      this.pager = this.controller.pagination({ options: this.options });
      this.urlManager = this.controller.urlManager({
        initialState: { fragment: window.location.search }
      });
      this.urlManager.subscribe(() => {
        const fragment = this.urlManager.state.fragment;
        const url = mergeSearchParams(fragment);
        this.lastSyncedUrl = url.replace("?", "");
        if (this.isFirstUrlUpdate) {
          window.history.replaceState({}, "", url);
          this.isFirstUrlUpdate = false;
        } else {
          window.history.pushState({}, "", url);
        }
      });
      window.addEventListener("popstate", this.handlePopState);
      if (this.sortBy.length) {
        this.sortController = this.controller.sort();
        this.sortController.subscribe(this.updateSortBy.bind(this));
        this.sortBy.forEach((sort) => {
          sort.addEventListener("change", (e) => {
            this.sortController.sortBy(JSON.parse(e.target.value));
          });
        });
      }
      this.pagination.setPager(this.pager);
      this.pagination.addEventListener("page-change", this.handlePageChange);
      if (this.itemsPerPageSelect.length) {
        this.itemsPerPageSelect.forEach((item) => {
          item.addEventListener("change", this.handleItemsPerPageChange);
        });
      }
      this.controller.subscribe(async () => {
        if (!this.firstRender) {
          this.firstRender = true;
          return;
        }
        if (this.controller.state.isLoading) {
          if (!this.facets?.isShowingMoreValues) {
            this.setState("loading");
          }
          return;
        }
        if (!this.facets.isInitialized) {
          this.facets.init(this.controller.facetGenerator(), this.engine);
        }
        this.facets?.render();
        this.classList.toggle("has-selected-facets", this.facets.selectedFacetsCount() > 0);
        const currentProducts = this.controller.state.products;
        const productFingerprint = currentProducts.map((p) => p.permanentid || p.clickUri).join(",");
        const productsChanged = productFingerprint !== this.lastProductFingerprint;
        this.lastProductFingerprint = productFingerprint;
        this.products = currentProducts;
        if (productsChanged) {
          await this.renderProducts(currentProducts);
        }
        this.updatePagination();
        this.updatePageSizeSelector();
        this.facets.isShowingMoreValues = false;
        this.handleAfterRender?.();
        this.setState("reset");
      });
      this.initDrawerFacet();
    }
    disconnectedCallback() {
      window.removeEventListener("popstate", this.handlePopState);
      if (this.popstateTimeout) {
        clearTimeout(this.popstateTimeout);
      }
    }
    handlePageChange(event) {
      this.currentPage = event.detail.page;
      this.pager.selectPage(this.currentPage);
      this.scrollIntoAnchor();
    }
    handleItemsPerPageChange(event) {
      this.itemsPerPage = parseInt(event.target.value, 10);
      this.pager.setPageSize(this.itemsPerPage);
      this.scrollIntoAnchor();
    }
    handlePopState() {
      if (this.popstateTimeout) {
        clearTimeout(this.popstateTimeout);
      }
      this.popstateTimeout = setTimeout(() => {
        const currentUrl = window.location.search.replace("?", "");
        if (normalizeUrlParams(this.lastSyncedUrl) === normalizeUrlParams(currentUrl))
          return;
        try {
          this.lastSyncedUrl = currentUrl;
          this.urlManager.synchronize(currentUrl);
        } catch (error) {
          console.warn("Failed to sync URL on popstate:", error);
          this.lastSyncedUrl = null;
        }
      }, 50);
    }
    async renderProducts(loadedProducts) {
      this.setAttribute("data-count", "");
      this.products = loadedProducts;
      const cardProducts = await Promise.all(
        loadedProducts.map(async (product) => {
          if (!product.clickUri && !product) {
            return false;
          }
          const handle = product.clickUri.split("/").pop();
          const { currentCollectionHandle } = theme.routes;
          const linkToFetch = ["products", handle].filter(Boolean).join("/");
          const html = await fetch(`${Shopify.routes.root}${linkToFetch}?section_id=card-product`).then(
            (res) => res.text()
          );
          const div = document.createElement("div");
          div.innerHTML = html;
          const productCard = div.querySelector("card-product");
          if (!productCard) {
            return false;
          }
          const productLinks = productCard.querySelectorAll("a[data-product-id]");
          const params = new URLSearchParams(window.location.search);
          const parentCollectionHandle = params.get("pc");
          productLinks.forEach((link) => {
            const url = new URL(link.href);
            if (parentCollectionHandle) {
              url.searchParams.set("pc", parentCollectionHandle);
            }
            if (currentCollectionHandle && currentCollectionHandle !== parentCollectionHandle) {
              url.searchParams.set("sc", currentCollectionHandle);
            }
            link.href = url.toString();
            link.addEventListener("click", async (e) => {
              const productId = link.dataset.productId;
              if (productId) {
                e.preventDefault();
                this.handleProductClick(productId);
                await delay(100);
              }
              location.href = link.href;
            });
          });
          return productCard;
        })
      );
      if (this.banner && cardProducts.length > this.bannerIndex && this.pager.state.page === 0) {
        cardProducts.splice(this.bannerIndex, 0, this.banner);
      }
      this.resultsContainer.innerHTML = "";
      this.resultsContainer.append(...cardProducts.filter(Boolean));
      this.setAttribute("data-count", this.resultsContainer.childElementCount);
      theme.customer.isB2B && window.PriceInventory.fetchLivePriceInventory();
      theme.customer.isLoggedIn && window.JobLists.checkAddedProducts();
    }
    updateSortBy() {
      try {
        const { appliedSort, availableSorts } = this.sortController.state;
        this.sortBy.forEach((sort) => {
          sort.innerHTML = "";
          availableSorts.forEach((sortOption, index) => {
            const { fields = [] } = sortOption;
            if (fields[0]?.name === "ec_price") {
              return;
            }
            const sortOptionElement = document.createElement("option");
            sortOptionElement.value = JSON.stringify(sortOption);
            sortOptionElement.innerHTML = this.getSortLabel(sortOption);
            if (JSON.stringify(appliedSort) === JSON.stringify(sortOption)) {
              sortOptionElement.selected = true;
            }
            sort.appendChild(sortOptionElement);
          });
        });
      } catch (error) {
        throw error;
      }
    }
    updatePagination() {
      const { totalEntries, page, pageSize } = this.pager.state;
      const displayCount = totalEntries > COVEO_MAX_RESULTS ? `${COVEO_MAX_RESULTS}+` : totalEntries;
      this.pagination.setAttribute("current-page", page);
      this.pagination.setAttribute("total-items", totalEntries);
      this.pagination.setAttribute("items-per-page", pageSize);
      const pageInfo = document.querySelectorAll(".js-pagination-info");
      pageInfo.forEach((v) => {
        switch (v.dataset.type) {
          case "total":
            v.innerHTML = theme.string.pagination.total[totalEntries > 1 ? "other" : "one"].replace(
              "{{ count }}",
              displayCount
            );
            break;
          case "showing":
            v.innerHTML = theme.string.pagination.showing.replace("{{ start }}", page * pageSize + 1).replace("{{ end }}", page * pageSize + pageSize).replace("{{ count }}", displayCount);
            break;
        }
      });
    }
    handleProductClick(productId) {
      const product = this.products.find((p) => p.ec_item_group_id === productId);
      if (product) {
        this.controller.interactiveProduct({ options: { product } }).select();
      }
    }
    getSortLabel(criterion) {
      switch (criterion.by) {
        case "relevance":
          return theme.string.coveo.sortBy.relevance;
        case "fields":
          return criterion.fields.map(this.formatSortFieldLabel);
      }
    }
    formatSortFieldLabel(field) {
      return field?.displayName ?? `${field.name} ${field.direction ?? ""}`.trim();
    }
    updatePageSizeSelector() {
    }
    setState(state) {
      switch (state) {
        case "loading":
          this.classList.add("is-loading");
          break;
        case "reset":
          this.classList.remove("is-loading");
          break;
      }
    }
    initDrawerFacet() {
      this.facetDrawer = this.querySelector("drawer-component");
      const triggerDrawerButtons = this.querySelectorAll(".js-drawer-button");
      if (!this.facetDrawer || !triggerDrawerButtons.length)
        return;
      triggerDrawerButtons.forEach((button) => {
        button.addEventListener("click", () => this.facetDrawer.open());
      });
      window.addEventListener("resize", () => {
        if (window.innerWidth > 1280) {
          this.facetDrawer.close();
        }
      });
    }
    scrollIntoAnchor() {
      const anchor = this.querySelector(".scroll-anchor");
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };
  function mergeSearchParams(fragment) {
    const params = new URLSearchParams(window.location.search);
    const data = {};
    fragment.split("&").forEach((part) => {
      const [key, value] = part.split("=");
      data[key] = value ?? "";
    });
    if (params.get("pc")) {
      data.pc = params.get("pc");
    }
    return "?" + Object.entries(data).map(([key, value]) => `${key}=${value}`).join("&");
  }
  function normalizeUrlParams(urlString) {
    if (!urlString)
      return "";
    const params = new URLSearchParams(urlString);
    return [...params.entries()].filter(([key]) => key).sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => `${key}=${value}`).join("&");
  }

  // app/scripts/coveo-product-listings.js
  var { loadProductListingActions } = coveoheadless;
  var CoveoProductListings = class extends CoveoBase {
    constructor(options = {}) {
      super(coveoheadless.buildProductListing);
      this.isFirstRender = false;
    }
    async connectedCallback() {
      await super.connectedCallback();
      const { fetchProductListing } = loadProductListingActions(this.engine);
      this.urlManager.synchronize(window.location.search.replace("?", ""));
      this.facets.onApplyFilters(() => {
        this.isFirstRender = true;
        this.setState("loading");
        this.engine.dispatch(fetchProductListing());
        this.facetDrawer.close();
      });
    }
    handleAfterRender() {
      this.classList.remove("is-first-render");
      this.isFirstRender && this.classList.remove("is-category-show");
    }
  };
  customElements.define("coveo-product-listings", CoveoProductListings);
})();
